// The single sanctioned home for all timezone math — see docs/timezones.md.
//
// Waypoint runs on ONE clock: the user's "home timezone" (the `displayTimezone`
// setting). Instants are stored/compared in UTC and only ever *rendered* in the
// home zone; civil dates (YYYY-MM-DD) are the home-zone day. Every part of the
// app that needs "what day/time is it" must go through this module rather than
// reading local `Date` fields directly (a convention — see docs/timezones.md).
//
// Pure and dependency-free: native `Intl` only (full ICU by default in Node and
// every modern browser), so it runs identically on the server and the client.
// One copy of this logic for the whole application: everything that reads a
// wall clock in a named zone comes here.

// Fixed and deliberately independent of APP_LOCALE: this only decomposes a
// wall-clock reading into numeric parts (read by `type`, never rendered or
// positionally parsed). Some locales default to a non-Gregorian calendar, which
// would corrupt the extracted year/month if this ever tracked the display locale.
const INTL_COMPUTE_LOCALE = "en-US";

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday .. 6 = Saturday
}

/** Decompose an instant into its wall-clock parts in `timeZone`. */
export function utcToZonedParts(instant: Date, timeZone: string): ZonedParts {
  const fmt = new Intl.DateTimeFormat(INTL_COMPUTE_LOCALE, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  });
  const parts = fmt.formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  let hour = parseInt(get("hour"), 10);
  if (hour === 24) hour = 0; // some runtimes emit "24" for midnight
  return {
    year: parseInt(get("year"), 10),
    month: parseInt(get("month"), 10),
    day: parseInt(get("day"), 10),
    hour,
    minute: parseInt(get("minute"), 10),
    second: parseInt(get("second"), 10),
    weekday: WEEKDAY_INDEX[get("weekday")] ?? 0,
  };
}

/**
 * Convert a wall-clock reading in `timeZone` to the UTC instant it denotes.
 *
 * Throws (via `Intl`) if `timeZone` isn't a recognised IANA name — callers that
 * must not throw guard with `isValidTimeZone` first, or use `isoDayInZone`.
 */
export function zonedWallClockToUtc(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const p = utcToZonedParts(new Date(utcGuess), timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const offset = asIfUtc - utcGuess;
  return new Date(utcGuess - offset);
}

/** Minutes `timeZone` is ahead of UTC at `instant` (e.g. +120 for CEST). */
export function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const p = utcToZonedParts(instant, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return (asIfUtc - instant.getTime()) / 60000;
}

/** True when `timeZone` is an IANA name this runtime's `Intl` accepts. */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The `YYYY-MM-DD` calendar day of `instant` in `timeZone` — the app's notion of
 * "which day is it". Falls back to the UTC day on an unknown zone rather than
 * throwing: the home zone is a user-editable setting read on hot paths, and a
 * bad value must degrade, not 500.
 */
export function isoDayInZone(instant: Date, timeZone: string): string {
  try {
    // `en-CA` is the locale that formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(instant);
  } catch {
    return instant.toISOString().slice(0, 10);
  }
}

// --- home-zone window boundaries, returned as UTC instants -------------------
// These return the UTC instant at which a home-zone day/week/month begins, so
// callers can `.toISOString()` them and compare as strings against stored UTC
// timestamps (which is how every range query in the app works).

/** UTC instant of home-zone midnight starting the civil day containing `instant`. */
export function startOfZonedDay(instant: Date, timeZone: string): Date {
  const p = utcToZonedParts(instant, timeZone);
  return zonedWallClockToUtc(p.year, p.month, p.day, 0, 0, 0, timeZone);
}

/** UTC instant of home-zone midnight on the Monday of `instant`'s civil week. */
export function startOfZonedWeek(instant: Date, timeZone: string): Date {
  const p = utcToZonedParts(instant, timeZone);
  const dow = (p.weekday + 6) % 7; // Mon=0 .. Sun=6
  // Shift the civil date back to Monday using a UTC scratch date for the
  // calendar arithmetic (crosses month/year cleanly), then resolve local midnight.
  const monday = new Date(Date.UTC(p.year, p.month - 1, p.day - dow));
  return zonedWallClockToUtc(
    monday.getUTCFullYear(),
    monday.getUTCMonth() + 1,
    monday.getUTCDate(),
    0,
    0,
    0,
    timeZone,
  );
}

/** UTC instant one week after `startOfZonedWeek` (exclusive week end). */
export function endOfZonedWeek(instant: Date, timeZone: string): Date {
  const start = startOfZonedWeek(instant, timeZone);
  const p = utcToZonedParts(start, timeZone);
  const next = new Date(Date.UTC(p.year, p.month - 1, p.day + 7));
  return zonedWallClockToUtc(
    next.getUTCFullYear(),
    next.getUTCMonth() + 1,
    next.getUTCDate(),
    0,
    0,
    0,
    timeZone,
  );
}

/** UTC instant of home-zone midnight on the 1st of `instant`'s civil month. */
export function startOfZonedMonth(instant: Date, timeZone: string): Date {
  const p = utcToZonedParts(instant, timeZone);
  return zonedWallClockToUtc(p.year, p.month, 1, 0, 0, 0, timeZone);
}

/** UTC instant of home-zone midnight on the 1st of the following civil month. */
export function endOfZonedMonth(instant: Date, timeZone: string): Date {
  const p = utcToZonedParts(instant, timeZone);
  const year = p.month === 12 ? p.year + 1 : p.year;
  const month = p.month === 12 ? 1 : p.month + 1;
  return zonedWallClockToUtc(year, month, 1, 0, 0, 0, timeZone);
}

/**
 * The runtime's own zone, as the neutral last-resort fallback — never a
 * hardcoded city. Node resolves this from the host; browsers from the OS. If
 * even that is somehow unavailable, `"UTC"` (a truth, not a location guess).
 */
export function resolveHostZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * The UTC instant a calendar-event reminder counts back from. Timed events:
 * the (occurrence's) stored instant. ALL-DAY events are zone-less — their
 * reminder fires relative to midnight of that civil day in the CURRENT home
 * zone `tz` (so a "1 hour" reminder lands at 23:00 the previous local
 * evening), never the UTC placeholder. See docs/timezones.md.
 *
 * Takes a structural `{ start, allDay }` rather than `CalendarEvent` itself —
 * Used identically by the reminder-picker UI and by the server's
 * `pollNotificationEvents`, so both evaluate reminders with the same
 * timezone math.
 */
export function eventAnchorMs(ev: { start: string; allDay: boolean }, tz: string): number {
  if (!ev.allDay) return Date.parse(ev.start);
  const [y, mo, d] = ev.start.slice(0, 10).split("-").map(Number);
  return zonedWallClockToUtc(y!, mo!, d!, 0, 0, 0, tz).getTime();
}
